import { useSyncExternalStore } from 'react'

type SessionState = {
  /** True when auth tokens exist in storage */
  isAuthenticated: boolean
  /** True once the user clears onboarding */
  hasCompletedOnboarding: boolean
}

type Listener = () => void
const listeners = new Set<Listener>()

const STORAGE_KEY = 'pixeloop/session-flags'
const defaultState: SessionState = {
  isAuthenticated: typeof window !== 'undefined' && !!localStorage.getItem('access'),
  hasCompletedOnboarding: false,
}

function readPersisted(): Pick<SessionState, 'hasCompletedOnboarding'> | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed.hasCompletedOnboarding === 'boolean') {
      return { hasCompletedOnboarding: parsed.hasCompletedOnboarding }
    }
  } catch {
    /* ignore */
  }
  return null
}

let state: SessionState = {
  ...defaultState,
  ...(readPersisted() || {}),
}

function persist(next: SessionState) {
  if (typeof window === 'undefined') return
  try {
    const payload = { hasCompletedOnboarding: next.hasCompletedOnboarding }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    /* ignore */
  }
}

function notify() {
  listeners.forEach((listener) => {
    try {
      listener()
    } catch {
      /* ignore individual listener errors */
    }
  })
}

function setState(updater: SessionState | ((prev: SessionState) => SessionState)) {
  const next = typeof updater === 'function' ? (updater as (p: SessionState) => SessionState)(state) : updater
  state = next
  persist(state)
  notify()
}

function subscribe(listener: Listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const getSnapshot = () => state

export function useSessionState() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export function getSessionState() {
  return state
}

export function setAuthenticated(value: boolean) {
  if (state.isAuthenticated === value) return
  setState({ ...state, isAuthenticated: value })
}

export function setHasCompletedOnboarding(value: boolean) {
  if (state.hasCompletedOnboarding === value) return
  setState({ ...state, hasCompletedOnboarding: value })
}

export function markOnboardingComplete(value = true) {
  setHasCompletedOnboarding(value)
}

export function useSessionFlags() {
  const { isAuthenticated, hasCompletedOnboarding } = useSessionState()
  return { isAuthenticated, hasCompletedOnboarding }
}
